// Prevent duplicate class declaration
if (typeof window.ShippingProgressManager === "undefined") {
  window.ShippingProgressManager = class ShippingProgressManager {
    constructor() {
      this.freeShippingThreshold = 50;
      this.isUpdating = false;
      this.updateTimeout = null;
      this.eventsInitialized = false;
      this.lastHasGeneral = undefined;
      this.init();
    }

    init() {
      this.bindEvents();
      setTimeout(() => this.updateAllProgressBars(), 100);
    }

    bindEvents() {
      if (this.eventsInitialized) {
        return;
      }

      // Listen to all cart-related events for real-time updates
      this.cartUpdatedHandler = () => this.debounceUpdate();
      document.addEventListener("cart:updated", this.cartUpdatedHandler);
      document.addEventListener("cart:refresh", this.cartUpdatedHandler);
      document.addEventListener("cart:change", this.cartUpdatedHandler);

      // Listen for cart drawer specific events
      document.addEventListener("cart-drawer:refresh", this.cartUpdatedHandler);
      document.addEventListener("cart-drawer:updated", this.cartUpdatedHandler);

      // Listen for dynamic price updates (when quantity changes)
      document.addEventListener("price:updated", this.cartUpdatedHandler);
      document.addEventListener("dynamic-price:updated", this.cartUpdatedHandler);

      // Shopify PUB_SUB events for cart updates
      if (typeof subscribe !== "undefined" && !this.pubSubSubscribed) {
        this.pubSubUnsubscriber = subscribe("cart-update", (event) => {
          this.debounceUpdate();
        });
        // Also subscribe to other cart events
        subscribe("cart:updated", (event) => {
          this.debounceUpdate();
        });
        subscribe("cart:refresh", (event) => {
          this.debounceUpdate();
        });
        this.pubSubSubscribed = true;
      }

      // Setup mutation observer for cart drawer changes
      this.setupMutationObserver();
      this.setupVariantEvents();
      this.setupCartFormEvents();

      this.eventsInitialized = true;
    }

    debounceUpdate() {
      // Clear any existing timeout
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }

      this.updateTimeout = setTimeout(() => {
        this.updateAllProgressBars();
        this.isUpdating = false;
        this.updateTimeout = null;
      }, 300); // Kürzere Wartezeit für bessere Responsivität
    }

    setupMutationObserver() {
      if (this.observer) {
        return;
      }

      // Observe cart drawer and other cart-related elements
      const observeTargets = [
        document.querySelector("cart-drawer, #CartDrawer"),
        document.querySelector(".cart-drawer__footer, .totals"),
        document.querySelector(".cart-items, #cart-items"),
        document.querySelector("[data-cart-total], .cart-total"),
      ].filter(Boolean);

      observeTargets.forEach((target) => {
        if (target) {
          const observer = new MutationObserver((mutations) => {
            let shouldUpdate = false;
            mutations.forEach((mutation) => {
              if (mutation.type === "childList" || mutation.type === "attributes") {
                // Update when cart content changes
                if (
                  mutation.target.closest(".cart-items, .cart-drawer__footer, .totals") ||
                  mutation.target.classList.contains("totals__total-value") ||
                  mutation.target.querySelector(".totals__total-value")
                ) {
                  shouldUpdate = true;
                }
              }
            });

            if (shouldUpdate) {
              this.debounceUpdate();
            }
          });

          observer.observe(target, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class", "data-cart-total"],
          });

          // Store observers for cleanup
          if (!this.observers) {
            this.observers = [];
          }
          this.observers.push(observer);
        }
      });
    }

    setupQuantityEvents() {
      // Remove these generic event listeners - let the product page handle quantity events
      // This prevents conflicts with the existing DynamicPrice class
    }

    setupVariantEvents() {
      const variantInputs = document.querySelectorAll('input[type="radio"], select.select__select');
      variantInputs.forEach((input) => {
        input.addEventListener("change", () => this.debounceUpdate());
      });

      // Also listen to quantity input changes for real-time updates
      const quantityInputs = document.querySelectorAll(
        'input[type="number"], .quantity__input, [name="quantity"]'
      );
      quantityInputs.forEach((input) => {
        input.addEventListener("input", () => this.debounceUpdate());
        input.addEventListener("change", () => this.debounceUpdate());
      });

      // Listen to quantity buttons (+/-)
      const quantityButtons = document.querySelectorAll(
        ".quantity__button, [data-quantity-button]"
      );
      quantityButtons.forEach((button) => {
        button.addEventListener("click", () => {
          // Update after a short delay to ensure quantity input value is updated
          setTimeout(() => this.debounceUpdate(), 50);
        });
      });
    }

    setupCartFormEvents() {
      // Listen for cart form submissions (add to cart)
      const cartForms = document.querySelectorAll(
        'form[action*="/cart/add"], .product-form, [id*="product-form"]'
      );
      cartForms.forEach((form) => {
        form.addEventListener("submit", () => {
          // Update progress bars after a short delay to ensure cart is updated
          setTimeout(() => this.debounceUpdate(), 500);
        });
      });

      // Listen for AJAX cart additions
      const addToCartButtons = document.querySelectorAll(
        '[name="add"], .btn-product-add, .product-form__cart-submit'
      );
      addToCartButtons.forEach((button) => {
        button.addEventListener("click", () => {
          // Update progress bars after a short delay to ensure cart is updated
          setTimeout(() => this.debounceUpdate(), 500);
        });
      });

      // Listen for cart drawer form changes
      const cartDrawerForm = document.querySelector("#CartDrawer-Form, .cart-drawer__form");
      if (cartDrawerForm) {
        cartDrawerForm.addEventListener("submit", () => {
          setTimeout(() => this.debounceUpdate(), 300);
        });
      }
    }

    getCurrentCartValue() {
      // Priority 1: Check cart drawer first (if user has cart open)
      const cartDrawer = document.querySelector("#CartDrawer, cart-drawer");
      // More precise cart drawer detection
      const isCartDrawerOpen =
        cartDrawer &&
        (cartDrawer.classList.contains("active") ||
          cartDrawer.hasAttribute("open") ||
          (cartDrawer.style.display !== "none" && cartDrawer.style.display !== "")) &&
        // Additional check: if the drawer is actually visible
        cartDrawer.offsetWidth > 0 &&
        cartDrawer.offsetHeight > 0;

      // Priority 2: Check if we're on a product page
      const isProductPage = document.querySelector(
        "product-info, .product-form, [data-product-id]"
      );

      // Get existing cart value first - always calculate this before any early returns
      let existingCartValue = 0;

      // Try to get cart data from Shopify cart API first
      if (window.cartData && window.cartData.total_price) {
        existingCartValue = window.cartData.total_price / 100;
      } else {
        // Fallback to DOM selectors for existing cart with better parsing
        const cartTotalSelectors = [
          ".totals__total-value",
          "[data-cart-total]",
          ".cart-total",
          ".cart__total",
          ".cart-drawer__total",
          "[data-cart-subtotal]",
          ".cart-subtotal",
          ".cart__subtotal",
        ];

        for (const selector of cartTotalSelectors) {
          const cartTotalElement = document.querySelector(selector);
          if (cartTotalElement) {
            const cartTotalText = cartTotalElement.textContent.trim();

            // Improved regex to match various currency formats
            // Matches: 24,99 €, 24.99 EUR, €24.99, $24.99, etc.
            const cartTotalMatch = cartTotalText.match(
              /(?:€\s*)?(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?)/
            );
            if (cartTotalMatch) {
              let value = cartTotalMatch[1].replace(",", ".");
              // Handle German number format (24,99 → 24.99)
              if (value.includes(",") && !value.includes(".")) {
                value = value.replace(",", ".");
              }
              const numericValue = parseFloat(value);

              if (numericValue >= 0) {
                existingCartValue = numericValue;
                break;
              }
            }
          }
        }
      }

      // Priority logic:
      // 1. If cart drawer is open AND has items, use only cart value (real-time cart management)
      if (isCartDrawerOpen && existingCartValue > 0) {
        return existingCartValue;
      }

      // 2. If we're on product page (regardless of drawer state when cart is empty), add product page value
      if (isProductPage && window.dynamicPriceCalculator) {
        const productPageValue = this.getProductPageValue();
        const totalValue = existingCartValue + productPageValue;

        return totalValue;
      }

      // 3. Fallback: use cart value only
      return existingCartValue;
    }

    getProductPageValue() {
      // Get current quantity and variant from product page
      const quantityInput = window.dynamicPriceCalculator?.quantityInput;
      if (!quantityInput) {
        return 0;
      }

      const quantity = parseInt(quantityInput.value) || 1;

      // Find selected variant
      let selectedId = null;
      const selectedVariantInput = document.querySelector(
        'input[type="radio"]:checked, select.select__select'
      );
      selectedId = selectedVariantInput?.dataset?.variantId || selectedVariantInput?.value;

      // Fallback: check for hidden variant input
      if (!selectedId) {
        const hiddenVariantInput = document.querySelector('input[name="id"]');
        selectedId = hiddenVariantInput?.value;
      }

      // Fallback: get first available variant from variantData
      if (!selectedId && window.variantData && window.variantData.length > 0) {
        selectedId = window.variantData[0].id;
      }

      if (window.variantData && selectedId) {
        const variantData = window.variantData.find((v) => v.id == selectedId);
        if (variantData) {
          const productValue = (variantData.price / 100) * quantity;

          return productValue;
        }
      }

      return 0;
    }

    getCurrentProductPrice() {
      // Get the price of a single unit of the current product
      let selectedId = null;
      const selectedVariantInput = document.querySelector(
        'input[type="radio"]:checked, select.select__select'
      );
      selectedId = selectedVariantInput?.dataset?.variantId || selectedVariantInput?.value;

      // Fallback: check for hidden variant input
      if (!selectedId) {
        const hiddenVariantInput = document.querySelector('input[name="id"]');
        selectedId = hiddenVariantInput?.value;
      }

      // Fallback: get first available variant from variantData
      if (!selectedId && window.variantData && window.variantData.length > 0) {
        selectedId = window.variantData[0].id;
      }

      if (window.variantData && selectedId) {
        const variantData = window.variantData.find((v) => v.id == selectedId);
        if (variantData) {
          return variantData.price / 100; // Single unit price
        }
      }

      return 0;
    }

    getCurrentQuantity() {
      // Get current quantity from product page
      const quantityInput = window.dynamicPriceCalculator?.quantityInput;
      if (quantityInput) {
        return parseInt(quantityInput.value) || 1;
      }

      // Fallback: try to find quantity input directly
      const quantityInputDirect = document.querySelector(
        'input[type="number"], .quantity__input, [name="quantity"]'
      );
      if (quantityInputDirect) {
        return parseInt(quantityInputDirect.value) || 1;
      }

      return 1; // Default quantity
    }

    getPureCartValue() {
      // Compute cart value considering only items with shipping profile 'general'
      let filteredValue = 0;

      const cartDrawer = document.querySelector("#CartDrawer, cart-drawer");
      const itemRows = cartDrawer?.querySelectorAll(
        ".cart-item[data-shipping-profile][data-line-price]"
      );
      if (itemRows && itemRows.length > 0) {
        itemRows.forEach((row) => {
          const profile = row.getAttribute("data-shipping-profile");
          const linePriceCents = parseInt(row.getAttribute("data-line-price")) || 0;
          if (profile === "general") {
            filteredValue += linePriceCents;
          }
        });
        filteredValue = filteredValue / 100;
      } else if (window.cartData && window.cartData.items) {
        // Fallback: if we have cartData, sum only qualifying items
        filteredValue =
          (window.cartData.items || []).reduce((sum, it) => {
            const tags = it.product_has_only_default_variant ? [] : []; // placeholder when tags unavailable
            return sum + it.final_line_price;
          }, 0) / 100;
      }

      return filteredValue;
    }

    getProductPageEnhancedValue() {
      // Always return cart value + product page value, regardless of drawer state
      const pureCartValue = this.getPureCartValue();
      const productPageValue = this.getProductPageValue();
      const totalValue = pureCartValue + productPageValue;

      return totalValue;
    }

    formatCurrency(amount) {
      // Convert to number if it's a string
      const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;
      if (Number.isNaN(numericAmount)) return "€0,00";

      // Format like other cart-drawer prices: "€25,42" (Euro first, comma decimals, dot thousands)
      const fixed = Math.abs(numericAmount).toFixed(2);
      const parts = fixed.split(".");
      const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      const decPart = parts[1];
      const sign = numericAmount < 0 ? "-" : "";
      return `${sign}€${intPart},${decPart}`;
    }

    updateProgressText(progressText, currentCartValue) {
      if (!progressText) return;

      // Check if this is the main product progress bar (has different styling)
      const isMainProduct = progressText.id === "shipping-progress-text";

      if (isMainProduct) {
        // Get pure cart value and product value separately
        const pureCartValue = this.getPureCartValue();
        const productValue = Math.max(0, currentCartValue - pureCartValue);

        // Check if the cart ALONE has already reached 100% (not preview)
        const cartAlreadyAt100 = pureCartValue >= this.freeShippingThreshold;

        if (cartAlreadyAt100) {
          // Cart has already reached 100%, show success message
          let freeShippingText =
            progressText.dataset.freeShippingText || "Kostenloser Versand freigeschaltet!";
          freeShippingText = freeShippingText.replace(
            /Kostenloser Versand/,
            '<span style="font-weight:700;color: rgb(131, 170, 143);">Kostenloser Versand<\/span>'
          );
          progressText.innerHTML = freeShippingText;
        } else {
          // Cart hasn't reached 100% yet, show preview formatting
          const formattedCart = this.formatCurrency(pureCartValue);
          const formattedTotal = this.formatCurrency(this.freeShippingThreshold);

          // Calculate how many products are needed to reach free shipping
          const remainingAmount = this.freeShippingThreshold - pureCartValue;
          const currentProductPrice = this.getCurrentProductPrice();
          const currentQuantity = this.getCurrentQuantity();

          // Calculate total products needed, then subtract current quantity to get additional needed
          const totalProductsNeeded =
            currentProductPrice > 0 ? Math.ceil(remainingAmount / currentProductPrice) : 0;
          const additionalProductsNeeded = Math.max(0, totalProductsNeeded - currentQuantity);

          // Check if adding current quantity will reach 100%
          const willReach100 = currentCartValue >= this.freeShippingThreshold;

          let productText, productColor;
          if (willReach100) {
            productText = progressText.dataset.willUnlockText || "You will unlock free shipping!";
            productColor = "rgb(131, 170, 143)"; // green color
          } else {
            // Determine language for appropriate text
            const isGerman =
              document.documentElement.lang === "de" || window.location.pathname.includes("/de/");
            const addText = isGerman
              ? `${additionalProductsNeeded} weitere Produkte hinzufügen`
              : `Add ${additionalProductsNeeded} more products`;
            const freeShippingText =
              progressText.dataset.forFreeShippingText ||
              (isGerman ? "für kostenlosen Versand!" : "for free shipping!");
            productText = `${addText} ${freeShippingText}`;
            productColor = "var(--primary-600)";
          }

          // New format: "X € already in Cart. Add Y more for free shipping"
          if (pureCartValue > 0) {
            const isGerman =
              document.documentElement.lang === "de" || window.location.pathname.includes("/de/");
            const alreadyInCartText = isGerman ? "bereits im Warenkorb" : "already in Cart";

            progressText.innerHTML = `
              <div style="display: flex; justify-content: space-between; align-items: baseline; width: 100%; gap: 0.5rem;">
                <span>
                  <span style="color: var(--primary-950); font-weight: 600;">${formattedCart}</span> ${alreadyInCartText}. 
                  <span style="color: ${productColor}; font-weight: 600;">${productText}</span>
                </span>
                <span style="background-color: var(--primary-950); color: white; padding: 4px 12px; border-radius: 9999px; font-size: 12px; font-weight: 600; flex-shrink: 0;">
                  ${formattedTotal}
                </span>
              </div>
            `;
          } else {
            // Cart is empty, show different message
            progressText.innerHTML = `
              <div style="display: flex; justify-content: space-between; align-items: baseline; width: 100%; gap: 0.5rem;">
                <span>
                  <span style="color: ${productColor}; font-weight: 600;">${productText}</span>
                </span>
                <span style="background-color: var(--primary-950); color: white; padding: 4px 12px; border-radius: 9999px; font-size: 12px; font-weight: 600; flex-shrink: 0;">
                  ${formattedTotal}
                </span>
              </div>
            `;
          }
        }
      } else {
        // Cart drawer logic
        if (currentCartValue >= this.freeShippingThreshold) {
          // Cart drawer has reached 100%
          let freeShippingText =
            progressText.dataset.freeShippingText || "Kostenloser Versand freigeschaltet!";
          freeShippingText = freeShippingText.replace(
            /Kostenloser Versand/,
            '<span style="font-weight:700;color: rgb(131, 170, 143);">Kostenloser Versand<\/span>'
          );
          progressText.innerHTML = freeShippingText;
          const priceBadge = document.getElementById("cart-drawer-shipping-price");
          if (priceBadge) {
            const base = parseFloat(priceBadge.dataset.shippingBase || "4.99");
            priceBadge.textContent = "Kostenlos";
            priceBadge.dataset.currentShipping = String(0);
            priceBadge.dataset.baseShipping = String(base);
          }
        } else {
          // Original logic for cart drawer
          const remaining = (this.freeShippingThreshold - currentCartValue).toFixed(2);
          const formattedAmount = this.formatCurrency(remaining);

          // Get translated template from data attribute or fallback
          const remainingTemplate =
            progressText.dataset.remainingTemplate || "Noch {{amount}} für kostenlosen Versand";
          const remainingText = remainingTemplate.replace(
            "{{amount}}",
            `<span class=\"tw-font-semibold tw-text-mine-900\">${formattedAmount}</span>`
          );
          progressText.innerHTML = remainingText;
          const priceBadge = document.getElementById("cart-drawer-shipping-price");
          if (priceBadge) {
            const base = parseFloat(priceBadge.dataset.shippingBase || "4.99");
            priceBadge.textContent = this.formatCurrency(base);
            priceBadge.dataset.currentShipping = String(base);
            priceBadge.dataset.baseShipping = String(base);
          }
        }
      }
    }

    updateProgressBar(progressBar, progressText, currentCartValue) {
      if (!progressBar || !progressText) return;

      const progress = Math.min((currentCartValue / this.freeShippingThreshold) * 100, 100);

      progressBar.style.width = progress + "%";

      // Update truck icon position
      const truckIcon = progressBar.parentElement.querySelector(
        'svg[id*="truck-icon"], i[id*="truck-icon"]'
      );
      if (truckIcon) {
        if (progress >= 95) {
          // Hide truck icon when progress reaches 95%
          //truckIcon.style.opacity = "0";
          // Still position the icon even when >= 95%
          const offset = progressBar.id.includes("cart-drawer") ? 36 : 6;
          const newLeft = `calc(${progress}% - ${offset}px)`;
          truckIcon.style.left = newLeft;
        } else {
          // Show truck icon and position it at the end of the progress bar
          truckIcon.style.opacity = "1";
          const offset = progressBar.id.includes("cart-drawer") ? 36 : 6; // Different offsets for different bar heights
          const newLeft = `calc(${progress}% - ${offset}px)`;
          truckIcon.style.left = newLeft;
        }
      }

      if (currentCartValue >= this.freeShippingThreshold) {
        // Get translated text from data attribute or fallback
        let freeShippingText =
          progressText.dataset.freeShippingText || "Kostenloser Versand freigeschaltet!";
        freeShippingText = freeShippingText.replace(
          /Kostenloser Versand/,
          '<span style="font-weight:700;color: rgb(131, 170, 143);">Kostenloser Versand<\/span>'
        );
        progressText.innerHTML = freeShippingText;
        progressBar.style.backgroundColor = "rgb(131, 170, 143)"; // green-500

        // Update price badge: show "Kostenlos" when free shipping is unlocked
        const priceBadge = document.getElementById("cart-drawer-shipping-price");
        if (priceBadge) {
          const base = parseFloat(priceBadge.dataset.shippingBase || "4.99");
          priceBadge.textContent = "Kostenlos";
          priceBadge.classList.remove(
            "tw-bg-merino-100",
            "tw-text-stardust",
            "tw-border-iron",
            "!tw-font-normal"
          );
          priceBadge.classList.add(
            "tw-bg-bayleaf",
            "tw-text-merino-100",
            "tw-font-bold",
            "tw-border-bayleaf",
            "!tw-font-semibold"
          );
        }
      } else {
        const remaining = (this.freeShippingThreshold - currentCartValue).toFixed(2);

        // Format currency based on locale/language
        const formattedAmount = this.formatCurrency(remaining);

        // Get translated template from data attribute or fallback
        const remainingTemplate =
          progressText.dataset.remainingTemplate || "Noch {{amount}} für kostenlosen Versand";
        const remainingText = remainingTemplate.replace(
          "{{amount}}",
          `<span class="tw-font-semibold tw-text-mine-900">${formattedAmount}</span>`
        );
        progressText.innerHTML = remainingText;
        progressBar.style.backgroundColor = ""; // Reset to CSS class

        // Update price badge: always show base price, default styling
        const priceBadge = document.getElementById("cart-drawer-shipping-price");
        if (priceBadge) {
          const base = parseFloat(priceBadge.dataset.shippingBase || "4.99");
          priceBadge.textContent = `– ${this.formatCurrency(base)}`;
          priceBadge.classList.remove(
            "tw-bg-bayleaf",
            "tw-text-merino-100",
            "tw-font-bold",
            "tw-border-bayleaf",
            "!tw-font-semibold"
          );
          priceBadge.classList.add(
            "tw-bg-merino-100",
            "tw-text-stardust",
            "tw-border-iron",
            "!tw-font-normal"
          );
        }
      }
    }

    updateTaxNotes(currentCartValue) {
      const taxNotes = document.querySelectorAll(".tax-note[data-tax-note-free]");
      taxNotes.forEach((taxNote) => {
        const threshold = parseFloat(
          taxNote.dataset.freeShippingThreshold || this.freeShippingThreshold
        );
        if (currentCartValue >= threshold) {
          taxNote.textContent = taxNote.dataset.taxNoteFree;
        } else if (taxNote.dataset.taxNoteDefault) {
          taxNote.innerHTML = taxNote.dataset.taxNoteDefault;
        }
      });
    }

    updateAllProgressBars() {
      // Get different values for different progress bars
      const pureCartValue = this.getPureCartValue(); // Only cart value, no product page addition
      const productPageEnhancedValue = this.getProductPageEnhancedValue(); // Always cart + product page value

      this.updateTaxNotes(pureCartValue);

      const productProgressBar = document.getElementById("shipping-progress-bar");
      const productProgressText = document.getElementById("shipping-progress-text");

      const cartDrawerProgressBar = document.getElementById("cart-drawer-shipping-progress-bar");
      const cartDrawerProgressText = document.getElementById("cart-drawer-shipping-progress-text");

      // Product page has two separate bars: cart-only and product-only
      if (productProgressBar && productProgressText) {
        // Calculate product-only value (enhanced - pure cart value)
        const productOnlyValue = Math.max(0, productPageEnhancedValue - pureCartValue);
        const productOnlyProgress = Math.min(
          (productOnlyValue / this.freeShippingThreshold) * 100,
          100
        );

        // Calculate progress values
        const cartOnlyProgress = Math.min((pureCartValue / this.freeShippingThreshold) * 100, 100);
        const totalProgress = Math.min(
          (productPageEnhancedValue / this.freeShippingThreshold) * 100,
          100
        );

        // Update cart-only bar
        const cartOnlyProgressBar = document.getElementById("shipping-cart-only-bar");
        if (cartOnlyProgressBar) {
          if (totalProgress >= 100) {
            // When 100% reached, show full green cart bar
            cartOnlyProgressBar.style.width = "100%";
            cartOnlyProgressBar.style.backgroundColor = "rgb(131, 170, 143)"; // green-500
            cartOnlyProgressBar.classList.remove("tw-rounded-l-full");
            cartOnlyProgressBar.classList.add("tw-rounded-full");
          } else {
            // Normal state - show cart progress
            cartOnlyProgressBar.style.width = cartOnlyProgress + "%";
            cartOnlyProgressBar.style.backgroundColor = ""; // Reset to CSS class
            cartOnlyProgressBar.classList.remove("tw-rounded-full");
            cartOnlyProgressBar.classList.add("tw-rounded-l-full");
          }
        }

        // Update the product bar to start where cart bar ends
        if (totalProgress >= 100) {
          // Hide product bar when 100% reached
          productProgressBar.style.width = "0%";
          productProgressBar.style.left = "0%";
        } else {
          // Ensure product bar doesn't exceed 100% total
          const maxProductProgress = Math.max(0, 100 - cartOnlyProgress);
          const adjustedProductProgress = Math.min(productOnlyProgress, maxProductProgress);

          productProgressBar.style.width = adjustedProductProgress + "%";
          productProgressBar.style.left = cartOnlyProgress + "%";

          // Adjust rounding based on cart status
          if (cartOnlyProgress === 0) {
            // Cart is empty, product bar should be fully rounded
            productProgressBar.classList.remove("tw-rounded-r-full");
            productProgressBar.classList.add("tw-rounded-full");
          } else {
            // Cart has content, product bar should only be rounded on the right
            productProgressBar.classList.remove("tw-rounded-full");
            productProgressBar.classList.add("tw-rounded-r-full");
          }
        }

        // Update truck icon to show total progress
        const truckIcon = productProgressBar.parentElement.querySelector(
          'svg[id*="truck-icon"], i[id*="truck-icon"]'
        );
        if (truckIcon) {
          const totalProgress = Math.min(
            (productPageEnhancedValue / this.freeShippingThreshold) * 100,
            100
          );

          if (totalProgress >= 95) {
            //truckIcon.style.opacity = "0";
            // Still position the icon even when >= 95%
            const offset = 12;
            const newLeft = `calc(${totalProgress}% - ${offset}px)`;
            truckIcon.style.left = newLeft;
          } else {
            truckIcon.style.opacity = "1";
            const offset = 6;
            const newLeft = `calc(${totalProgress}% - ${offset}px)`;
            truckIcon.style.left = newLeft;
          }
        }

        // Update text based on total progress
        this.updateProgressText(productProgressText, productPageEnhancedValue);
      }

      // Toggle visibility based on presence of at least one 'general' profile item
      const progressContainer = document.getElementById("cart-drawer-shipping-progress");
      const hasGeneral = (() => {
        const cartDrawer = document.querySelector("#CartDrawer, cart-drawer");
        if (!cartDrawer) return false;
        const rows = cartDrawer.querySelectorAll(".cart-item[data-shipping-profile]");
        if (!rows || rows.length === 0) return false;
        for (const row of rows) {
          if (row.getAttribute("data-shipping-profile") === "general") return true;
        }
        return false;
      })();

      if (progressContainer) {
        if (hasGeneral) {
          progressContainer.style.display = "";
        } else {
          progressContainer.style.display = "none";
        }
        this.lastHasGeneral = hasGeneral;
      }

      // Cart drawer progress bar uses filtered cart value only
      if (cartDrawerProgressBar && cartDrawerProgressText && hasGeneral) {
        this.updateProgressBar(cartDrawerProgressBar, cartDrawerProgressText, pureCartValue);
      }

      // If cart drawer elements not found, try to find them with alternative selectors
      if (!cartDrawerProgressBar || !cartDrawerProgressText) {
        const alternativeProgressBar = document.querySelector(
          "#CartDrawer #cart-drawer-shipping-progress-bar, cart-drawer #cart-drawer-shipping-progress-bar"
        );
        const alternativeProgressText = document.querySelector(
          "#CartDrawer #cart-drawer-shipping-progress-text, cart-drawer #cart-drawer-shipping-progress-text"
        );

        if (alternativeProgressBar && alternativeProgressText) {
          this.updateProgressBar(alternativeProgressBar, alternativeProgressText, pureCartValue);
        }
      }
    }
  };

  // Initialize Cart Drawer Swiper
  window.initCartDrawerSwiper = function initCartDrawerSwiper() {
    const cartDrawerSwiper = document.querySelector(".cart-drawer-swiper");
    if (!cartDrawerSwiper) {
      return;
    }

    // Destroy existing swiper instance if it exists
    if (cartDrawerSwiper.swiper) {
      cartDrawerSwiper.swiper.destroy(true, true);
    }

    const swiper = new Swiper(".cart-drawer-swiper", {
      slidesPerView: 1,
      spaceBetween: 30,
      loop: true,
      /*autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },*/
      navigation: {
        nextEl: ".cart-drawer-swiper .swiper-button-next-new",
        prevEl: ".cart-drawer-swiper .swiper-button-prev-new",
      },
      pagination: {
        el: ".cart-drawer-swiper .swiper-pagination",
        clickable: true,
        bulletClass:
          "swiper-pagination-bullet tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-950/20 tw-transition-all tw-duration-300 tw-cursor-pointer tw-inline-block",
        bulletActiveClass: "tw-bg-primary-950",
      },
      effect: "slide",
      speed: 500,
      on: {
        init: function () {},
      },
    });
  };

  // Initialize Cart Upsell Variant Slider Swiper
  window.initCartUpsellSwiper = function initCartUpsellSwiper() {
    const upsellContainer = document.querySelector(".cart-drawer-swiper");
    const upsellSwiper = document.querySelector(".cart-upsell-variant-swiper");
    const upsellSliderContainer = document.querySelector("[data-upsell-container]");
    
    if (!upsellSwiper || !upsellContainer || !upsellSliderContainer) {
      return;
    }

    // Ensure container is visible before initializing
    if (upsellSliderContainer.style.display === "none") {
      return; // Don't initialize if hidden
    }

    // Skip if already initialized and visible (prevents blink on multiple calls)
    if (upsellSwiper.swiper && (upsellSliderContainer.classList.contains("cart-upsell-fade-in") || upsellSliderContainer.classList.contains("cart-upsell-ready"))) {
      return;
    }

    // Skip if currently initializing
    if (window._upsellSwiperInitializing) {
      return;
    }
    window._upsellSwiperInitializing = true;

    // Destroy existing swiper instance if it exists
    if (upsellSwiper.swiper) {
      upsellSwiper.swiper.destroy(true, true);
    }

    // Small delay to ensure DOM is ready
    setTimeout(() => {
      const swiper = new Swiper(".cart-upsell-variant-swiper", {
        slidesPerView: 3,
        spaceBetween: 2,
        loop: true,
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
        },
        watchOverflow: true,
        // navigation: {
        //   nextEl: upsellContainer.querySelector(".swiper-button-next"),
        //   prevEl: upsellContainer.querySelector(".swiper-button-prev"),
        // },
        pagination: {
          el: upsellContainer.querySelector(".swiper-pagination"),
          clickable: true,
          bulletClass:
            "swiper-pagination-bullet tw-w-2 tw-h-2 tw-rounded-full tw-bg-mine-950/20 tw-transition-all tw-duration-300 tw-cursor-pointer tw-inline-block",
          bulletActiveClass: "tw-bg-mine-950",
        },
        effect: "slide",
        speed: 500,
        on: {
          init: function() {
            const swiperInstance = this;
            // Force update after initialization to recalculate sizes
            if (swiperInstance && swiperInstance.update) {
              swiperInstance.update();
              swiperInstance.updateSlides();
              swiperInstance.updateSlidesClasses();
            }
            // Trigger fade-in animation after swiper is fully ready
            // Use requestAnimationFrame to ensure layout is complete
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (upsellSliderContainer) {
                  upsellSliderContainer.classList.remove("cart-upsell-hidden");
                  upsellSliderContainer.classList.add("cart-upsell-fade-in");
                  
                  // After animation completes, switch to stable ready state
                  setTimeout(() => {
                    if (upsellSliderContainer) {
                      upsellSliderContainer.classList.remove("cart-upsell-fade-in");
                      upsellSliderContainer.classList.add("cart-upsell-ready");
                    }
                  }, 500); // Match animation duration
                }
                // Clear initializing flag after animation starts
                window._upsellSwiperInitializing = false;
              });
            });
          }
        }
      });
    }, 50);
  };

  // Initialize cart drawer swiper when DOM is loaded and when cart drawer opens
  document.addEventListener("DOMContentLoaded", function () {
    setTimeout(() => {
      if (window.initCartDrawerSwiper) window.initCartDrawerSwiper();
      if (window.initCartUpsellSwiper) window.initCartUpsellSwiper();
    }, 100);
  });

  // Re-initialize when cart drawer opens (in case it wasn't loaded initially)
  document.addEventListener("cart:open", function () {
    setTimeout(() => {
      if (window.initCartDrawerSwiper) window.initCartDrawerSwiper();
      if (window.initCartUpsellSwiper) window.initCartUpsellSwiper();
    }, 200);
  });

  // Re-initialize when cart is updated (especially when going from empty to non-empty)
  document.addEventListener("cart:updated", function () {
    setTimeout(() => {
      if (window.initCartDrawerSwiper) window.initCartDrawerSwiper();
      if (window.initCartUpsellSwiper) window.initCartUpsellSwiper();
    }, 300);
  });

  // Also listen to cart refresh events
  document.addEventListener("cart:refresh", function () {
    setTimeout(() => {
      if (window.initCartDrawerSwiper) window.initCartDrawerSwiper();
      if (window.initCartUpsellSwiper) window.initCartUpsellSwiper();
    }, 300);
  });

  // Listen to Shopify PUB_SUB cart events (used by existing cart code)
  if (typeof subscribe !== "undefined") {
    subscribe("cart-update", function (event) {
      setTimeout(() => {
        if (window.initCartDrawerSwiper) window.initCartDrawerSwiper();
        if (window.initCartUpsellSwiper) window.initCartUpsellSwiper();
      }, 400);
    });
  }

  // Global function for cart drawer quick add using existing cart drawer API
  window.addToCartDrawer = async function (variantId, button, event) {
    // Stop event propagation to prevent link navigation
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const originalText = button.innerHTML;

    // Show loading state
    button.disabled = true;
    button.classList.add("loading");
    const addingText = button.dataset.addingText || "Adding...";
    button.innerHTML = `<i class="fa-regular fa-spinner fa-spin tw-text-xs"></i> ${addingText}`;

    try {
      // Get the cart drawer element
      const cartDrawer = document.querySelector("cart-drawer");
      if (!cartDrawer) {
        throw new Error("Cart drawer not found");
      }

      // Create form data
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", "1");

      // Add sections data for cart drawer update like the existing cart code does
      formData.append(
        "sections",
        cartDrawer.getSectionsToRender().map((section) => section.id)
      );
      formData.append("sections_url", window.location.pathname);
      cartDrawer.setActiveElement(document.activeElement);

      // Configure the request like existing cart code
      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = formData;

      // Submit to cart API
      const response = await fetch(`${routes.cart_add_url}`, config);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const cartData = await response.json();

      // Store cart data globally
      window.cartData = cartData;

      // Publish cart update event like existing code
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "cart-drawer-slider",
        productVariantId: variantId,
        cartData: cartData,
      });

      // Update the cart drawer using the existing method
      cartDrawer.renderContents(cartData);

      // Remove empty state if present
      if (cartDrawer.classList.contains("is-empty")) {
        cartDrawer.classList.remove("is-empty");
      }

      // Re-initialize swiper immediately after cart drawer content is updated
      setTimeout(() => {
        if (window.initCartDrawerSwiper) {
          window.initCartDrawerSwiper();
        }
        if (window.initCartUpsellSwiper) {
          window.initCartUpsellSwiper();
        }
      }, 200);

      // Force immediate update of progress bars with a slight delay to ensure DOM is updated
      setTimeout(() => {
        if (window.shippingProgressManager) {
          window.shippingProgressManager.updateAllProgressBars();
        }
      }, 100);

      // Also trigger another update after a longer delay to be safe
      setTimeout(() => {
        if (window.shippingProgressManager) {
          window.shippingProgressManager.updateAllProgressBars();
        }
      }, 500);

      // Show success state
      button.innerHTML = '<i class="fa-regular fa-check tw-text-xs"></i> Hinzugefügt!';
      button.classList.remove("tw-bg-primary-950", "hover:tw-bg-primary-800");
      button.classList.add("tw-bg-green-600");

      // Reset button after 2 seconds
      setTimeout(() => {
        button.disabled = false;
        button.classList.remove("loading");
        button.innerHTML = originalText;
        button.classList.remove("tw-bg-green-600");
        button.classList.add("tw-bg-primary-950", "hover:tw-bg-primary-800");

        // Re-initialize swiper after cart update
        setTimeout(() => {
          if (window.initCartDrawerSwiper) window.initCartDrawerSwiper();
          if (window.initCartUpsellSwiper) window.initCartUpsellSwiper();
        }, 100);
      }, 2000);
    } catch (error) {
      console.error("Error adding to cart:", error);

      // Show error state
      button.innerHTML = '<i class="fa-regular fa-exclamation-triangle tw-text-xs"></i> Fehler';
      button.classList.remove("tw-bg-primary-950", "hover:tw-bg-primary-800");
      button.classList.add("tw-bg-red-600");

      // Reset button after 2 seconds
      setTimeout(() => {
        button.disabled = false;
        button.classList.remove("loading");
        button.innerHTML = originalText;
        button.classList.remove("tw-bg-red-600");
        button.classList.add("tw-bg-primary-950", "hover:tw-bg-primary-800");
      }, 2000);
    }
  };

  // Global function for cart drawer upsell quick add with thank you message
  window.addToCartDrawerUpsell = async function (variantId, button, event) {
    // Stop event propagation to prevent link navigation
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const originalText = button.innerHTML;

    // Show loading state
    button.disabled = true;
    button.classList.add("loading");
    const addingText = button.dataset.addingText || "Adding...";
    button.innerHTML = `<i class="fa-regular fa-spinner fa-spin tw-text-xs"></i> ${addingText}`;

    try {
      // Get the cart drawer element
      const cartDrawer = document.querySelector("cart-drawer");
      if (!cartDrawer) {
        throw new Error("Cart drawer not found");
      }

      // Store reference to upsell container before DOM replacement
      const originalUpsellContainer = cartDrawer.querySelector("[data-upsell-container]");
      const hadUpsell = !!originalUpsellContainer;
      
      // Store thank you message data globally so it persists after renderContents
      let thankYouData = null;
      if (hadUpsell) {
        // Hide the upsell content immediately
        const upsellContent = originalUpsellContainer.querySelector("[data-upsell-content]");
        if (upsellContent) {
          upsellContent.style.display = "none";
        }
        
        // Store thank you message data globally
        thankYouData = {
          messageText: 'Das hat geklappt, der Rabatt wurde automatisch aktiviert.',
          startTime: Date.now()
        };
        
        // Store on window so it persists
        window._upsellThankYouData = thankYouData;
      }

      // Create form data
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", "1");

      // Add sections data for cart drawer update
      formData.append(
        "sections",
        cartDrawer.getSectionsToRender().map((section) => section.id)
      );
      formData.append("sections_url", window.location.pathname);
      cartDrawer.setActiveElement(document.activeElement);

      // Configure the request
      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = formData;

      // Submit to cart API
      const response = await fetch(`${routes.cart_add_url}`, config);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const cartData = await response.json();

      // Store cart data globally
      window.cartData = cartData;

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "cart-drawer-upsell",
        productVariantId: variantId,
        cartData: cartData,
      });

      // Update the cart drawer
      cartDrawer.renderContents(cartData);

      // Remove empty state if present
      if (cartDrawer.classList.contains("is-empty")) {
        cartDrawer.classList.remove("is-empty");
      }

      // After renderContents, re-insert thank you message immediately
      // Use the stored data from window
      const storedThankYouData = window._upsellThankYouData || thankYouData;
      
      if (storedThankYouData) {
        // Function to insert and manage thank you message
        const insertThankYouMessage = () => {
          // Find cart-drawer-items or cart items wrapper to insert after
          const cartDrawerItems = cartDrawer.querySelector("cart-drawer-items");
          const cartItemsForm = cartDrawer.querySelector("#CartDrawer-Form");
          const footer = cartDrawer.querySelector(".drawer__footer");
          
          let insertTarget = null;
          let insertBefore = footer;
          
          if (cartDrawerItems) {
            insertTarget = cartDrawerItems;
          } else if (cartItemsForm) {
            insertTarget = cartItemsForm.parentNode;
          } else {
            insertTarget = cartDrawer.querySelector(".drawer__inner");
          }
          
          if (insertTarget) {
            // Create thank you element
            const thankYouElement = document.createElement("div");
            thankYouElement.className = "cart-upsell-thank-you-standalone tw-px-[2.4rem] tw-p-4 tw-mx-auto";
            thankYouElement.style.width = "100%";
            thankYouElement.style.maxWidth = "100%";
            thankYouElement.style.animation = "fadeIn 0.3s ease-in";
            thankYouElement.style.marginTop = "2.4rem";
            thankYouElement.style.marginBottom = "0";
            thankYouElement.style.border = "1px solid rgb(131 170 143)";
            thankYouElement.style.borderLeft = "none";
            thankYouElement.style.borderRight = "none";
            thankYouElement.style.borderBottom = "none";
            thankYouElement.style.backgroundColor = "#DEE2D7";
            thankYouElement.style.position = "absolute";
            thankYouElement.style.bottom = "67.5px";
            thankYouElement.style.left = "0";
            thankYouElement.style.right = "0";
            thankYouElement.style.zIndex = "1000";
            thankYouElement.innerHTML = `
              <div class="tw-flex tw-items-center tw-gap-3">
                <svg class="tw-w-6 tw-h-6 tw-text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <div>
                  <p class="tw-text-sm tw-m-0" style="color: rgb(131 170 143);">${storedThankYouData.messageText}</p>
                </div>
              </div>
            `;
            
            // Insert before footer or at the end
            if (insertBefore && insertBefore.parentNode === insertTarget) {
              insertTarget.insertBefore(thankYouElement, insertBefore);
            } else {
              insertTarget.appendChild(thankYouElement);
            }
            
            // Calculate remaining time to ensure full 5 seconds from start
            const elapsed = Date.now() - storedThankYouData.startTime;
            const remainingTime = Math.max(3000 - elapsed, 3000); // At least 3 seconds
            
            // Hide upsell container if it still exists (with fade-out animation)
            const upsellContainer = cartDrawer.querySelector("[data-upsell-container]");
            if (upsellContainer && upsellContainer.parentNode) {
              upsellContainer.classList.remove("cart-upsell-fade-in");
              upsellContainer.classList.add("cart-upsell-fade-out");
              // Wait for animation to complete before hiding
              setTimeout(() => {
                if (upsellContainer && upsellContainer.parentNode) {
                  upsellContainer.style.display = "none";
                  upsellContainer.classList.remove("cart-upsell-fade-out");
                }
              }, 600); // Match animation duration
            }
            
            // Hide thank you message after remaining time (full 5 seconds from start)
            setTimeout(() => {
              if (thankYouElement && thankYouElement.parentNode) {
                thankYouElement.style.animation = "fadeOut 0.5s ease-out forwards";
                setTimeout(() => {
                  if (thankYouElement && thankYouElement.parentNode) {
                    thankYouElement.remove();
                  }
                  // Clean up
                  delete window._upsellThankYouData;
                }, 500);
              }
            }, remainingTime);
          }
        };
        
        // Insert immediately after renderContents (with small delay to ensure DOM is ready)
        setTimeout(insertThankYouMessage, 100);
      } else if (hadUpsell) {
        // If thank you wasn't shown before renderContents, try to show it after
        let retryCount = 0;
        const maxRetries = 20;
        
        const showThankYouMessage = () => {
          const upsellContainer = cartDrawer.querySelector("[data-upsell-container]");
          
          if (!upsellContainer && retryCount < maxRetries) {
            retryCount++;
            setTimeout(showThankYouMessage, 150);
            return;
          }

          if (upsellContainer) {
            const thankYouMessage = upsellContainer.querySelector("[data-upsell-thank-you]");
            const upsellContent = upsellContainer.querySelector("[data-upsell-content]");

            if (thankYouMessage && upsellContent) {
              upsellContent.style.display = "none";
              thankYouMessage.classList.remove("tw-hidden");
              thankYouMessage.style.display = "block";
              
              // Start timer from when message is actually shown
              const showTime = Date.now();
              setTimeout(() => {
              if (upsellContainer && upsellContainer.parentNode) {
                upsellContainer.classList.remove("cart-upsell-fade-in");
                upsellContainer.classList.add("cart-upsell-fade-out");
                // Wait for animation to complete before hiding
                setTimeout(() => {
                  if (upsellContainer && upsellContainer.parentNode) {
                    upsellContainer.style.display = "none";
                    upsellContainer.classList.remove("cart-upsell-fade-out");
                  }
                }, 600); // Match animation duration
              }
              }, 5000);
            }
          }
        };

        setTimeout(showThankYouMessage, 600);
      }

      // Re-initialize swiper after cart drawer content is updated
      setTimeout(() => {
        if (window.initCartDrawerSwiper) {
          window.initCartDrawerSwiper();
        }
        if (window.initCartUpsellSwiper) {
          window.initCartUpsellSwiper();
        }
      }, 200);

      // Update progress bars
      setTimeout(() => {
        if (window.shippingProgressManager) {
          window.shippingProgressManager.updateAllProgressBars();
        }
      }, 100);

      setTimeout(() => {
        if (window.shippingProgressManager) {
          window.shippingProgressManager.updateAllProgressBars();
        }
      }, 500);

      // Reset button state (button was replaced by renderContents, so this may not work)
      // The button will be reset when the DOM is re-rendered
    } catch (error) {
      console.error("Error adding to cart:", error);

      // Show error state
      button.innerHTML = '<i class="fa-regular fa-exclamation-triangle tw-text-xs"></i> Fehler';
      button.classList.remove("tw-bg-primary-950", "hover:tw-bg-primary-800");
      button.classList.add("tw-bg-red-600");

      // Reset button after 2 seconds
      setTimeout(() => {
        button.disabled = false;
        button.classList.remove("loading");
        button.innerHTML = originalText;
        button.classList.remove("tw-bg-red-600");
        button.classList.add("tw-bg-primary-950", "hover:tw-bg-primary-800");
      }, 2000);
    }
  };
} // End of ShippingProgressManager class conditional

// Initialize everything on page load
document.addEventListener("DOMContentLoaded", function () {
  if (!window.shippingProgressManager) {
    window.shippingProgressManager = new window.ShippingProgressManager();
  }
  setTimeout(() => {
    if (window.initCartDrawerSwiper) window.initCartDrawerSwiper();
    if (window.initCartUpsellSwiper) window.initCartUpsellSwiper();
  }, 100);
});
